import { useState } from "react";
import { Input, Text, View, XStack, YStack } from "tamagui";
import { RiFireLine, RiWallet3Line } from "react-icons/ri";

import { colors } from "@styles/colors";
import { pointsToNtd, type PointsKind } from "@utils/points";
import { POINTS_TINT } from "./pointsTint";

/**
 * 呼叫端的皮。兩條下單流程的結帳卡長得不一樣，這一列要跟著各自的鄰居走：
 * 瓦斯訂購那張卡裡「優惠券／支付方式」都是一行值、沒有圖示、沒有分隔線；
 * Kiosk 那張卡的每一列則是 icon + 標籤 + 值。同一支元件、兩種身段。
 * 點數的識別色（琥珀／藍）不在皮裡：那是點數的身分，兩邊都該一致。
 */
export interface PointsRowSkin {
  /** 主文字 */
  ink: string;
  /** 次要文字 */
  sub: string;
  /** 分隔線顏色 */
  line: string;
  /** 可點文字（「全部用完」）的顏色 */
  accent: string;
  /** 是否顯示左側圖示方塊（跟著宿主卡片其他列的做法） */
  icon?: boolean;
}

/** 開關。沿用 Account ToggleRow 的尺寸與動態，開啟時吃該種點數的色。 */
const Toggle = ({
  value,
  onChange,
  disabled,
  accent,
}: {
  value: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
  accent: string;
}) => (
  <View
    w={44}
    h={26}
    br={13}
    bg={value ? accent : colors.gray100}
    position="relative"
    flexShrink={0}
    opacity={disabled ? 0.45 : 1}
    cursor={disabled ? "default" : "pointer"}
    onPress={disabled ? undefined : () => onChange(!value)}
    style={{ transition: "background 0.2s" }}>
    <View
      w={22}
      h={22}
      br={11}
      bg={colors.light}
      position="absolute"
      t={2}
      l={value ? 20 : 2}
      style={{ transition: "left 0.2s", boxShadow: "0 2px 4px rgba(0,0,0,0.15)" }}
    />
  </View>
);

const META: Record<PointsKind, { label: string; scope: string; icon: (c: string) => JSX.Element }> = {
  stored_gas: { label: "存氣點數", scope: "只折瓦斯", icon: (c) => <RiFireLine size={16} color={c} /> },
  account: { label: "帳戶點數", scope: "全單可折", icon: (c) => <RiWallet3Line size={16} color={c} /> },
};

interface PointsRedeemRowProps {
  kind: PointsKind;
  /** 餘額（點） */
  balance: number;
  /** 目前折抵（點），已由呼叫端 clamp */
  used: number;
  /** 這張單可折的上限（點） */
  max: number;
  /** 算費已完成（false 時開關停用，避免用還沒算準的上限去折） */
  ready: boolean;
  onChange: (points: number) => void;
  skin: PointsRowSkin;
  isLast?: boolean;
  /**
   * 意願模式：這張單的金額由瓦斯行接單後才提供（隱藏金額／專員報價），
   * 折抵算不出金額，所以只收「要不要折」——不顯示折抵金額，也沒有可調點數。
   * 傳入時 used / max 不參與渲染。
   */
  intent?: { on: boolean; onToggle: (on: boolean) => void };
}

/**
 * 一種點數的折抵列。關著時就是一行（標題 + 餘額 + 用途 + 開關），
 * 與「優惠券」「支付方式」同高；打開後才長出第二行讓人改點數。
 * 兩種點數的差別（只折瓦斯／全單可折）縮成標題後面那一小段，不另闢說明行——
 * 同一張卡裡其他欄位都沒有說明行，多這兩行就是整段看起來最亂的原因。
 */
const PointsRedeemRow = ({ kind, balance, used, max, ready, onChange, skin, isLast, intent }: PointsRedeemRowProps) => {
  // 輸入中允許暫時是空字串（不然打字時清空會立刻被填回 0，游標也跟著跳）
  const [draft, setDraft] = useState<string | null>(null);
  const t = POINTS_TINT[kind];
  const meta = META[kind];
  const on = intent ? intent.on : used > 0;
  // 餘額有、但這張單折不到（例：券已把瓦斯折完）→ 開關關掉並說明原因，別讓人以為壞了。
  // 意願模式沒有金額可比，不會有這個狀態。
  const unusable = !intent && ready && max <= 0;

  const commit = (text: string) => {
    setDraft(text);
    const n = Number(text.replace(/[^\d]/g, ""));
    onChange(Number.isFinite(n) ? Math.min(Math.max(0, Math.floor(n)), max) : 0);
  };

  return (
    /* 啟用中不再鋪底色：開關本身、右邊冒出來的折抵金額、底下多出來的那行輸入，
       三個訊號已經把「這列是開著的」講得很清楚。兩列同時開啟時，兩條滿版色帶會疊成
       一塊雙色橫紋 —— 那是整個 App 沒有第二個地方長的樣子，也是這張卡看起來怪的原因。 */
    <YStack py={11} bbw={!isLast ? 1 : 0} bbc={skin.line}>
      <XStack ai="center" gap={skin.icon ? 12 : 10}>
        {skin.icon && (
          <View w={32} h={32} br={8} bg={t.tile} ai="center" jc="center" flexShrink={0}>
            {meta.icon(t.icon)}
          </View>
        )}
        <XStack f={1} miw={0} ai="center" gap={7}>
          <Text fontSize={14} fontWeight="600" color={skin.ink} flexShrink={0}>
            {meta.label}
          </Text>
          <Text fontSize={12} color={skin.sub} numberOfLines={1} style={{ fontVariantNumeric: "tabular-nums" }}>
            {unusable ? "本單無可折金額" : `${balance.toLocaleString()} 點 · ${meta.scope}`}
          </Text>
        </XStack>
        {/* 意願模式沒有金額可寫；一般模式才在開關左邊顯示這一列折了多少錢 */}
        {!intent && on && (
          <Text fontSize={14} fontWeight="800" color={t.icon} flexShrink={0} mr={2} style={{ fontVariantNumeric: "tabular-nums" }}>
            −NT$ {pointsToNtd(used).toLocaleString()}
          </Text>
        )}
        <Toggle
          value={on}
          accent={t.icon}
          disabled={intent ? false : !ready || unusable}
          onChange={(v) => {
            if (intent) {
              intent.onToggle(v);
              return;
            }
            setDraft(null);
            onChange(v ? max : 0);
          }}
        />
      </XStack>

      {/* 意願模式：說清楚何時折、最多折多少。客戶是在看不到金額的情況下同意的，
          寫「使用點數」太含糊 —— 得讓他知道上限就是自己的餘額。 */}
      {intent && (
        <Text fontSize={11} color={skin.sub} mt={4} ml={skin.icon ? 44 : 0}>
          瓦斯行確認金額後自動折抵，最多 {balance.toLocaleString()} 點
        </Text>
      )}

      {/* 第二行只在開啟時出現：多數人只會整筆折掉，不必平時就把輸入框攤在畫面上 */}
      {!intent && on && (
        <XStack ai="center" gap={8} mt={8} ml={skin.icon ? 44 : 0}>
          <Text fontSize={12} color={skin.sub}>
            折抵
          </Text>
          <Input
            value={draft ?? String(used)}
            onChangeText={commit}
            onBlur={() => setDraft(null)}
            inputMode="numeric"
            keyboardType="number-pad"
            h={30}
            w={62}
            px={8}
            fontSize={14}
            fontWeight="700"
            color={skin.ink}
            ta="center"
            bg={colors.light}
            borderColor={skin.line}
            borderWidth={1}
            borderRadius={8}
          />
          <Text fontSize={12} color={skin.sub}>
            點{used < max ? ` / 上限 ${max.toLocaleString()}` : ""}
          </Text>
          {/* 沒折滿時給一個「全部用完」的快捷 —— 手機上要把 210 一個字一個字打出來太費事 */}
          {used < max && (
            <Text
              fontSize={12}
              fontWeight="700"
              color={skin.accent}
              ml="auto"
              cursor="pointer"
              pressStyle={{ opacity: 0.6 }}
              onPress={() => {
                setDraft(null);
                onChange(max);
              }}>
              全部用完
            </Text>
          )}
        </XStack>
      )}
    </YStack>
  );
};

export default PointsRedeemRow;
